import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { addStep, cleanDomain, type DreamCompany } from "../utils/dreamCompanies";
import { detectBoard } from "../utils/dreamApi";
import { FormDialog, Field } from "./dreamShared";
import { MELT_INPUT, MELT_TEXTAREA } from "./ui/melt";

export type { DreamCompany };


type AddDreamCompanyProps = {
  isOpen: boolean;
  onClose: () => void;
  editCompany?: DreamCompany;
};

export default function AddDreamCompany({ isOpen, onClose, editCompany }: AddDreamCompanyProps) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setName(editCompany?.name || "");
    setDomain(editCompany?.domain || "");
    setNextStep("");
    setNotes(editCompany?.notes || "");
    setError("");
  }, [editCompany, isOpen]);

  const handleSubmit = async () => {
    const user = auth.currentUser;
    if (!user) {
      setError("Please log in to save companies.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      // Cleared fields are saved empty, so editing can remove them
      const companyData = {
        name: name.trim(),
        domain: cleanDomain(domain),
        notes: notes.trim(),
      };

      if (editCompany) {
        await updateDoc(doc(db, "users", user.uid, "dreamCompanies", editCompany.id), companyData);
        // A new name or website can mean a different board; without a board, saving looks again
        if (!editCompany.boardKey || companyData.name !== editCompany.name || companyData.domain !== (editCompany.domain || "")) {
          detectBoard(editCompany.id);
        }
      } else {
        const added = await addDoc(collection(db, "users", user.uid, "dreamCompanies"), {
          ...companyData,
          steps: addStep([], nextStep),
          createdAt: serverTimestamp(),
        });
        detectBoard(added.id);
      }
      onClose();
    } catch (e: any) {
      console.error("Error saving dream company:", e);
      setError("Couldn’t save the company. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={isOpen}
      onClose={onClose}
      title={editCompany ? "Edit company" : "Add a dream company"}
      description={
        editCompany ? "Update the details for this company." : "Follow a company you’d love to work at."
      }
      submitLabel={saving ? "Saving…" : editCompany ? "Save changes" : "Add company"}
      canSubmit={Boolean(name.trim()) && cleanDomain(domain).includes(".")}
      submitting={saving}
      onSubmit={handleSubmit}
    >
      <Field id="dream-name" label="Company" required>
        <input
          id="dream-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Stripe"
          className={MELT_INPUT}
          autoFocus
        />
      </Field>

      <Field id="dream-domain" label="Website" required>
        <input
          id="dream-domain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="e.g. stripe.com"
          className={MELT_INPUT}
        />
      </Field>

      {/* Only when adding: after that, steps are added and ticked off in the company panel */}
      {!editCompany && (
        <Field id="dream-next" label="Next step">
          <input
            id="dream-next"
            value={nextStep}
            onChange={(e) => setNextStep(e.target.value)}
            placeholder="e.g. Ask Sara for a referral"
            className={MELT_INPUT}
          />
        </Field>
      )}

      <Field id="dream-notes" label="Notes">
        <textarea
          id="dream-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything worth remembering…"
          rows={3}
          className={MELT_TEXTAREA}
        />
      </Field>

      {error && (
        <div className="rounded-[10px] border border-[#F0CFC2] bg-[#FBEFEA] px-3.5 py-2.5 text-sm text-[#9A4F33]">{error}</div>
      )}
    </FormDialog>
  );
}
